/**
 * Resolves once Office.js has called its onReady handler. All Office-aware
 * code paths (Office.context, Office.dialog, ...) must wait on this.
 */
export const officeReady: Promise<Office.HostType | null> = new Promise((resolve) => {
  if (typeof Office === "undefined") {
    resolve(null);
    return;
  }
  void Office.onReady((info) => {
    resolve(info.host);
  });
});

// PowerPointApi requirement-set versions whose features the agent might lean
// on. We probe in descending order to find the highest supported set, then
// pass it (plus concrete capability flags) to the server so the model can
// pick a path that actually works on this user's PowerPoint build.
const POWERPOINT_API_VERSIONS = [
  "1.10",
  "1.9",
  "1.8",
  "1.7",
  "1.6",
  "1.5",
  "1.4",
  "1.3",
  "1.2",
  "1.1",
] as const;

export type Platform = "PC" | "Mac" | "OfficeOnline" | "iOS" | "Android" | "unknown";

export type HostCapabilities = {
  host: "PowerPoint" | "unknown";
  powerPointApiVersion: string | null;
  platform: Platform;
  officeVersion: string | null;
  // shapes.addPicture (preview) or ShapeFill.setImage fallback (1.8+).
  canInsertImage: boolean;
  // Slide.getImageAsBase64 — PowerPointApi 1.8 (peek_slide / getSlideImage).
  canGetSlideImage: boolean;
  // ShapeCollection.addTable — PowerPointApi 1.8.
  canAddTable: boolean;
  // ShapeCollection.addGroup — PowerPointApi 1.8.
  canAddGroup: boolean;
  // Slide.applyLayout — PowerPointApi 1.8 (via oj.call; no pp.applyLayout helper).
  canApplyLayout: boolean;
  // Shape.placeholderFormat — PowerPointApi 1.8.
  canReadPlaceholderFormat: boolean;
  // Slide.themeColorScheme / slideMasters theme reads — treat as 1.8+.
  canReadTheme: boolean;
};

const detectPlatform = (): Platform => {
  if (typeof Office === "undefined" || !Office.context) return "unknown";
  const p = Office.context.platform;
  if (p === Office.PlatformType.PC) return "PC";
  if (p === Office.PlatformType.Mac) return "Mac";
  if (p === Office.PlatformType.OfficeOnline) return "OfficeOnline";
  if (p === Office.PlatformType.iOS) return "iOS";
  if (p === Office.PlatformType.Android) return "Android";
  return "unknown";
};

const emptyCapabilities = (): HostCapabilities => ({
  host: "unknown",
  powerPointApiVersion: null,
  platform: "unknown",
  officeVersion: null,
  canInsertImage: false,
  canGetSlideImage: false,
  canAddTable: false,
  canAddGroup: false,
  canApplyLayout: false,
  canReadPlaceholderFormat: false,
  canReadTheme: false,
});

const probeCanInsertImage = async (): Promise<boolean> => {
  try {
    return await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items");
      await context.sync();
      const slide = slides.items[0];
      if (!slide) return false;
      const shapes = slide.shapes as unknown as {
        addPicture?: unknown;
      };
      if (typeof shapes.addPicture === "function") return true;
      // GA fallback: fill an existing/new shape with a picture (1.8+).
      return (
        typeof Office !== "undefined" &&
        !!Office.context?.requirements?.isSetSupported("PowerPointApi", "1.8")
      );
    });
  } catch {
    return false;
  }
};

export const detectHostCapabilities = async (): Promise<HostCapabilities> => {
  await officeReady;
  if (
    typeof Office === "undefined" ||
    !Office.context ||
    !Office.context.requirements
  ) {
    return emptyCapabilities();
  }
  const req = Office.context.requirements;
  let version: string | null = null;
  for (const v of POWERPOINT_API_VERSIONS) {
    if (req.isSetSupported("PowerPointApi", v)) {
      version = v;
      break;
    }
  }
  const atLeast = (target: string): boolean =>
    version !== null && req.isSetSupported("PowerPointApi", target);

  const canGetSlideImage = atLeast("1.8");
  // 1.8+: ShapeFill.setImage fallback. Preview hosts may expose addPicture
  // without advertising 1.8 — probe for that.
  const canInsertImage = atLeast("1.8") ? true : await probeCanInsertImage();

  return {
    host: "PowerPoint",
    powerPointApiVersion: version,
    platform: detectPlatform(),
    officeVersion: Office.context.diagnostics?.version ?? null,
    canInsertImage,
    canGetSlideImage,
    canAddTable: atLeast("1.8"),
    canAddGroup: atLeast("1.8"),
    canApplyLayout: atLeast("1.8"),
    canReadPlaceholderFormat: atLeast("1.8"),
    canReadTheme: atLeast("1.8"),
  };
};
